/**
 * WhatsApp media utility functions for file handling and validation
 * Provides MIME type detection, validation, and formatting for WhatsApp media messages
 */

/**
 * WhatsApp media type enum matching backend types
 */
export enum WhatsAppMediaType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  STICKER = 'sticker',
}

/**
 * WhatsApp media size limits in bytes (matching backend limits)
 */
export const WHATSAPP_MEDIA_LIMITS = {
  [WhatsAppMediaType.IMAGE]: {
    maxSize: 5 * 1024 * 1024, // 5MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
  },
  [WhatsAppMediaType.VIDEO]: {
    maxSize: 16 * 1024 * 1024, // 16MB
    allowedTypes: ['video/mp4', 'video/3gp', 'video/3gpp'],
  },
  [WhatsAppMediaType.AUDIO]: {
    maxSize: 16 * 1024 * 1024, // 16MB
    allowedTypes: [
      'audio/aac',
      'audio/mp4',
      'audio/mpeg',
      'audio/amr',
      'audio/ogg',
    ],
  },
  [WhatsAppMediaType.DOCUMENT]: {
    maxSize: 100 * 1024 * 1024, // 100MB
    allowedTypes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/csv',
    ],
  },
  [WhatsAppMediaType.STICKER]: {
    maxSize: 500 * 1024, // 500KB
    allowedTypes: ['image/webp'],
  },
} as const;

/**
 * Interface for media validation result
 */
export interface MediaValidationResult {
  isValid: boolean;
  mediaType: WhatsAppMediaType;
  error?: string;
}

/**
 * Interface for file data needed for validation
 */
export interface FileForValidation {
  name: string;
  type: string;
  size: number;
}

/**
 * Determines WhatsApp media type based on MIME type
 * @param contentType - MIME type of the file
 * @returns WhatsApp media type
 *
 * @example
 * ```ts
 * getWhatsAppMediaType('image/jpeg') // returns WhatsAppMediaType.IMAGE
 * getWhatsAppMediaType('image/webp') // returns WhatsAppMediaType.STICKER
 * getWhatsAppMediaType('video/mp4') // returns WhatsAppMediaType.VIDEO
 * ```
 */
export const getWhatsAppMediaType = (
  contentType: string
): WhatsAppMediaType => {
  if (contentType.startsWith('image/')) {
    return contentType === 'image/webp'
      ? WhatsAppMediaType.STICKER
      : WhatsAppMediaType.IMAGE;
  }
  if (contentType.startsWith('video/')) {
    return WhatsAppMediaType.VIDEO;
  }
  if (contentType.startsWith('audio/')) {
    return WhatsAppMediaType.AUDIO;
  }
  return WhatsAppMediaType.DOCUMENT;
};

/**
 * Validates if a MIME type is supported for a specific WhatsApp media type
 * @param contentType - MIME type to validate
 * @param mediaType - WhatsApp media type
 * @returns true if MIME type is supported
 *
 * @example
 * ```ts
 * isValidMimeTypeForMedia('image/jpeg', WhatsAppMediaType.IMAGE) // returns true
 * isValidMimeTypeForMedia('image/gif', WhatsAppMediaType.IMAGE) // returns false
 * ```
 */
export const isValidMimeTypeForMedia = (
  contentType: string,
  mediaType: WhatsAppMediaType
): boolean => {
  const limits = WHATSAPP_MEDIA_LIMITS[mediaType];
  return (limits.allowedTypes as readonly string[]).includes(contentType);
};

/**
 * Validates if file size is within WhatsApp limits for a specific media type
 * @param fileSize - Size of the file in bytes
 * @param mediaType - WhatsApp media type
 * @returns true if file size is within limits
 *
 * @example
 * ```ts
 * isValidFileSize(1024 * 1024, WhatsAppMediaType.IMAGE) // returns true (1MB image)
 * isValidFileSize(10 * 1024 * 1024, WhatsAppMediaType.IMAGE) // returns false (10MB image, exceeds 5MB limit)
 * ```
 */
export const isValidFileSize = (
  fileSize: number,
  mediaType: WhatsAppMediaType
): boolean => {
  const limits = WHATSAPP_MEDIA_LIMITS[mediaType];
  return fileSize <= limits.maxSize;
};

/**
 * Formats file size in bytes to human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 *
 * @example
 * ```ts
 * formatFileSize(1024) // returns "1 KB"
 * formatFileSize(1536) // returns "1.5 KB"
 * formatFileSize(1048576) // returns "1 MB"
 * formatFileSize(0) // returns "0 Bytes"
 * ```
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Validates a file for WhatsApp media requirements
 * @param file - File object or file data to validate
 * @returns Validation result with media type and any error
 *
 * @example
 * ```ts
 * const result = validateWhatsAppMediaFile(selectedFile);
 * if (result.isValid) {
 *   console.log(`File is valid ${result.mediaType} media`);
 * } else {
 *   console.error(`Validation failed: ${result.error}`);
 * }
 * ```
 */
export const validateWhatsAppMediaFile = (
  file: File | FileForValidation
): MediaValidationResult => {
  const fileData = {
    name: file.name,
    type: file.type,
    size: file.size,
  };

  const mediaType = getWhatsAppMediaType(fileData.type);

  // Check MIME type support
  if (!isValidMimeTypeForMedia(fileData.type, mediaType)) {
    const allowedTypes = WHATSAPP_MEDIA_LIMITS[mediaType].allowedTypes;
    return {
      isValid: false,
      mediaType,
      error: `File type '${fileData.type}' is not supported for ${mediaType}. Allowed types: ${allowedTypes.join(', ')}`,
    };
  }

  // Check file size
  if (!isValidFileSize(fileData.size, mediaType)) {
    const maxSize = WHATSAPP_MEDIA_LIMITS[mediaType].maxSize;
    return {
      isValid: false,
      mediaType,
      error: `File size ${formatFileSize(fileData.size)} exceeds maximum allowed size of ${formatFileSize(maxSize)} for ${mediaType}`,
    };
  }

  // Additional note for stickers
  if (mediaType === WhatsAppMediaType.STICKER) {
    return {
      isValid: true,
      mediaType,
      error: 'Note: Stickers should be 512x512 pixels for optimal display',
    };
  }

  return {
    isValid: true,
    mediaType,
  };
};

/**
 * Gets the appropriate file input accept attribute for WhatsApp media types
 * @param mediaTypes - Array of media types to accept, or 'all' for all types
 * @returns accept attribute string for file input
 *
 * @example
 * ```ts
 * getFileInputAccept([WhatsAppMediaType.IMAGE, WhatsAppMediaType.VIDEO])
 * // returns "image/jpeg,image/png,image/webp,video/mp4,video/3gp,video/3gpp"
 *
 * getFileInputAccept('all')
 * // returns all supported MIME types
 * ```
 */
export const getFileInputAccept = (
  mediaTypes: WhatsAppMediaType[] | 'all'
): string => {
  if (mediaTypes === 'all') {
    return Object.values(WHATSAPP_MEDIA_LIMITS)
      .flatMap((limit) => limit.allowedTypes)
      .join(',');
  }

  return mediaTypes
    .flatMap((type) => WHATSAPP_MEDIA_LIMITS[type].allowedTypes)
    .join(',');
};

/**
 * Gets maximum file size for a WhatsApp media type
 * @param mediaType - WhatsApp media type
 * @returns Maximum file size in bytes
 *
 * @example
 * ```ts
 * getMaxFileSizeForMediaType(WhatsAppMediaType.IMAGE) // returns 5242880 (5MB)
 * getMaxFileSizeForMediaType(WhatsAppMediaType.DOCUMENT) // returns 104857600 (100MB)
 * ```
 */
export const getMaxFileSizeForMediaType = (
  mediaType: WhatsAppMediaType
): number => {
  return WHATSAPP_MEDIA_LIMITS[mediaType].maxSize;
};

/**
 * Gets allowed MIME types for a WhatsApp media type
 * @param mediaType - WhatsApp media type
 * @returns Array of allowed MIME types
 *
 * @example
 * ```ts
 * getAllowedMimeTypesForMediaType(WhatsAppMediaType.IMAGE)
 * // returns ['image/jpeg', 'image/png', 'image/webp']
 * ```
 */
export const getAllowedMimeTypesForMediaType = (
  mediaType: WhatsAppMediaType
): readonly string[] => {
  return WHATSAPP_MEDIA_LIMITS[mediaType].allowedTypes;
};

/**
 * Creates a user-friendly error message for validation failures
 * @param result - Validation result from validateWhatsAppMediaFile
 * @returns User-friendly error message
 *
 * @example
 * ```ts
 * const result = validateWhatsAppMediaFile(file);
 * if (!result.isValid) {
 *   const errorMessage = createValidationErrorMessage(result);
 *   showToast(errorMessage, 'error');
 * }
 * ```
 */
export const createValidationErrorMessage = (
  result: MediaValidationResult
): string => {
  if (result.isValid) return '';

  return result.error ?? `Invalid ${result.mediaType} file`;
};

/**
 * Gets a media type icon emoji for display purposes
 * @param mediaType - WhatsApp media type
 * @returns Emoji string representing the media type
 *
 * @example
 * ```ts
 * getMediaTypeIcon(WhatsAppMediaType.IMAGE) // returns "🖼️"
 * getMediaTypeIcon(WhatsAppMediaType.VIDEO) // returns "🎥"
 * ```
 */
export const getMediaTypeIcon = (mediaType: WhatsAppMediaType): string => {
  switch (mediaType) {
    case WhatsAppMediaType.IMAGE:
      return '🖼️';
    case WhatsAppMediaType.VIDEO:
      return '🎥';
    case WhatsAppMediaType.AUDIO:
      return '🎵';
    case WhatsAppMediaType.DOCUMENT:
      return '📄';
    case WhatsAppMediaType.STICKER:
      return '🎨';
    default:
      return '📎';
  }
};

/**
 * Checks if a file is supported by WhatsApp (quick validation without detailed errors)
 * @param file - File object to check
 * @returns true if file is supported, false otherwise
 *
 * @example
 * ```ts
 * if (isWhatsAppMediaSupported(selectedFile)) {
 *   // Proceed with upload
 * } else {
 *   // Show error message
 * }
 * ```
 */
export const isWhatsAppMediaSupported = (file: File): boolean => {
  const result = validateWhatsAppMediaFile(file);
  return result.isValid;
};
